import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Alert, Tab, Tabs, InputGroup, FormControl, Badge, Row, Col, Form, Collapse } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { listStudents } from '../../api/authApi';
import { setCompanyShortlists } from '../../api/preferencesApi';
import { getAllCompanies } from '../../api/companyApi';
import ManagePortalAccess from '../../components/admin/ManagePortalAccess';
import ShortlistStudents from '../../components/admin/ShortlistStudents';

const ManageStudents = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  const [selectedCompany, setSelectedCompany] = useState(null);
  // Track expanded student details
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchStudents();
    fetchCompanies();
  }, []);

  useEffect(() => {
    filterStudents();
  }, [students, searchTerm]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await listStudents();
      setStudents(data || []);
      setFilteredStudents(data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to fetch students. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filterStudents = () => {
    if (!searchTerm.trim()) {
      setFilteredStudents(students);
      return;
    }
    
    const filtered = students.filter(student => {
      const lowerSearchTerm = searchTerm.toLowerCase();
      return (
        student.name.toLowerCase().includes(lowerSearchTerm) ||
        student.email.toLowerCase().includes(lowerSearchTerm) ||
        (student.phone && student.phone.includes(searchTerm)) ||
        (student.hostel && student.hostel.toLowerCase().includes(lowerSearchTerm))
      );
    });
    
    setFilteredStudents(filtered);
  };

  const handleShortlistUpdate = async (payload) => {
    try {
      setLoading(true);
      
      console.log('Shortlist Update Payload:', payload);
      
      const response = await setCompanyShortlists(payload);
      
      console.log('Shortlist Update Response:', response);
      
      toast.success('Shortlists updated successfully');
      fetchStudents(); // Refresh data
    } catch (error) {
      console.error('Error updating shortlists:', error);
      toast.error('Failed to update shortlists');
    } finally {
      setLoading(false);
    }
  };
  const handleRefresh = () => {
    fetchStudents();
    toast.info('Refreshing student data...');
  };

  const toggleStudentDetails = (studentId) => {
    setExpandedStudent(expandedStudent === studentId ? null : studentId);
  };

  const fetchCompanies = async () => {
    try {
      const companiesData = await getAllCompanies();
      setCompanies(companiesData || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to fetch companies');
    }
  };

  const getUniqueCompanies = () => {
    const companies = new Set();
    students.forEach(student => {
      student.company_preferences?.forEach(pref => {
        companies.add(pref.company_name);
      });
    });
    return Array.from(companies).sort();
  };

  return (
    <Container className="py-4">
      <h1 className="mb-4">Manage Students</h1>
      
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="list" title="Student List">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">All Students</h5>
              <Button variant="outline-primary" size="sm" onClick={handleRefresh}>
                Refresh
              </Button>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <FormControl
                  placeholder="Search by name, email, phone or hostel..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
              
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading students...</p>
                </div>
              ) : filteredStudents.length === 0 ? (
                <Alert variant="info">
                  {searchTerm ? 'No students match your search.' : 'No students found.'}
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Contact</th>
                        <th>Status</th>
                        <th>Preferences</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.map((student) => (
                        <React.Fragment key={student.email}>
                          <tr>
                            <td>{student.name}</td>
                            <td>{student.email}</td>
                            <td>
                              {student.phone || 'N/A'}
                              <div className="small text-muted">
                                {student.hostel ? `${student.hostel}, ${student.room || 'N/A'}` : 'No hostel info'}
                              </div>
                            </td>
                            <td>
                              {student.registration_complete ? (
                                <Badge bg="success">Registered</Badge>
                              ) : (
                                <Badge bg="warning">Incomplete</Badge>
                              )}
                              <div className="mt-1">
                                {student.can_access_portal ? (
                                  <Badge bg="info">Has Access</Badge>
                                ) : (
                                  <Badge bg="danger">No Access</Badge>
                                )}
                              </div>
                            </td>
                            <td>
                              {student.company_preferences?.length > 0 ? (
                                <Badge bg="primary">
                                  {student.company_preferences.length} Companies
                                </Badge>
                              ) : (
                                <Badge bg="secondary">No Preferences</Badge>
                              )}
                              <div className="mt-1">
                                {student.company_preferences?.some(pref => pref.is_shortlisted) ? (
                                  <Badge bg="success">Shortlisted</Badge>
                                ) : null}
                              </div>
                            </td>
                            <td>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => toggleStudentDetails(student.id)}
                                aria-expanded={expandedStudent === student.id}
                              >
                                {expandedStudent === student.id ? 'Hide Details' : 'View Details'}
                              </Button>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="6" className="p-0">
                              <Collapse in={expandedStudent === student.id}>
                                <div>
                                  <Card className="border-info m-2">
                                    <Card.Header className="bg-info text-white">
                                      <h5 className="mb-0">{student.name} - Details</h5>
                                    </Card.Header>
                                    <Card.Body>
                                      <Row>
                                        <Col md={6}>
                                          <h6>Personal Information</h6>
                                          <Table bordered size="sm">
                                            <tbody>
                                              <tr>
                                                <th width="40%">Email</th>
                                                <td>{student.email}</td>
                                              </tr>
                                              <tr>
                                                <th>Phone</th>
                                                <td>{student.phone || 'Not provided'}</td>
                                              </tr>
                                              <tr>
                                                <th>Hostel</th>
                                                <td>{student.hostel || 'Not provided'}</td>
                                              </tr>
                                              <tr>
                                                <th>Room</th>
                                                <td>{student.room || 'Not provided'}</td>
                                              </tr>
                                            </tbody>
                                          </Table>
                                        </Col>
                                        <Col md={6}>
                                          <h6>Company Preferences</h6>
                                          {!student.company_preferences?.length ? (
                                            <Alert variant="secondary">No company preferences submitted</Alert>
                                          ) : (
                                            <Table bordered size="sm">
                                              <thead>
                                                <tr>
                                                  <th>Preference</th>
                                                  <th>Company</th>
                                                  <th>Status</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {student.company_preferences?.map((pref) => (
                                                  <tr key={`${student.id}-${pref.company_name}-${pref.preference_order}`}>
                                                    <td>{pref.preference_order}</td>
                                                    <td>{pref.company_name}</td>
                                                    <td>
                                                      {pref.is_shortlisted ? (
                                                        <Badge bg="success">Shortlisted</Badge>
                                                      ) : (
                                                        <Badge bg="secondary">Not Shortlisted</Badge>
                                                      )}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          )}
                                        </Col>
                                      </Row>
                                    </Card.Body>
                                  </Card>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="access" title="Portal Access">
          <ManagePortalAccess students={students} onAccessUpdated={fetchStudents} />
        </Tab>
        
        <Tab eventKey="shortlist" title="Shortlist Students">
          <Card>
            <Card.Header as="h5">Shortlist Students for Companies</Card.Header>
            <Card.Body>
              <Form.Group className="mb-4">
                <Form.Label>Select Company</Form.Label>
                <Form.Select 
                  value={selectedCompany || ''}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                >
                  <option value="">Select a company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.name}>
                      {company.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              {selectedCompany ? (
                <ShortlistStudents
                  companyName={selectedCompany}
                  companyId={companies.find(c => c.name === selectedCompany)?.id}
                  students={students} // Pass all students, not just those with preferences
                  onShortlistUpdate={handleShortlistUpdate}
                />
              ) : (
                <Alert variant="info">
                  Please select a company to manage shortlists
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default ManageStudents;